import { Controller } from '@hotwired/stimulus'

// Drives the owner.com-style "pinned visual" section: a sticky column of stacked
// visuals on one side, a sequence of text steps on the other. As each step scrolls
// through the activation band, its matching visual cross-fades into view.
//
// Usage:
//   <div data-controller="scroll-story">
//     <div class="scroll-story__steps">
//       <div data-scroll-story-target="step" data-index="0">...</div>
//       <div data-scroll-story-target="step" data-index="1">...</div>
//     </div>
//     <div class="scroll-story__visuals">
//       <div data-scroll-story-target="visual" data-index="0">...</div>
//       <div data-scroll-story-target="visual" data-index="1">...</div>
//     </div>
//   </div>
//
// Pure class toggling — CSS handles the actual transitions. On small screens the
// sticky/observer behaviour is disabled in CSS and every step shows its own inline
// visual, so the section degrades to a simple stacked list.
export default class extends Controller {
  static targets = ['step', 'visual']

  connect() {
    // First step/visual active by default so there's never a blank state.
    this.activate(0)

    if (!('IntersectionObserver' in window)) {
      return
    }

    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
      rootMargin: '0px 0px 0px 0px',
      threshold: [0, 0.25, 0.5, 0.75, 1.0]
    })

    this.stepTargets.forEach((step) => this.observer.observe(step))
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
  }

  // Click a step to bring it (and its visual) into view. Only on the desktop
  // sticky/observed layout; below lg every step shows its own inline visual so
  // there's nothing to scroll to. Links inside the step still navigate normally.
  select(event) {
    if (event.target.closest('a, button')) return
    if (!window.matchMedia('(min-width: 992px)').matches) return

    event.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  handleIntersect() {
    // Find all steps currently in the viewport
    const visibleSteps = this.stepTargets.filter((step) => {
      const rect = step.getBoundingClientRect()
      return rect.bottom > 0 && rect.top < window.innerHeight
    })

    if (visibleSteps.length === 0) return

    // Find the step whose center is closest to the viewport center
    const viewportCenter = window.innerHeight / 2
    let closestStep = visibleSteps[0]
    let minDistance = Infinity

    visibleSteps.forEach((step) => {
      const rect = step.getBoundingClientRect()
      const stepCenter = rect.top + rect.height / 2
      const distance = Math.abs(stepCenter - viewportCenter)
      if (distance < minDistance) {
        minDistance = distance
        closestStep = step
      }
    })

    this.activate(this.indexFor(closestStep))
  }

  activate(index) {
    this.stepTargets.forEach((step) => {
      step.classList.toggle('is-active', this.indexFor(step) === index)
    })
    this.visualTargets.forEach((visual) => {
      visual.classList.toggle('is-active', this.indexFor(visual) === index)
    })

    // Drive the progress rail fill (0 → 1) from the active step.
    const last = this.stepTargets.length - 1
    this.element.style.setProperty('--story-progress', last > 0 ? index / last : 0)
  }

  indexFor(element) {
    return parseInt(element.getAttribute('data-index'), 10) || 0
  }
}
